import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Role {
    id: string;
    name: string;
    description: string;
    permissions: { [key: string]: string[] };
    is_system_role: boolean;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface RoleListResponse {
    roles: Role[];
    total: number;
    page: number;
    per_page: number;
}

export interface RoleCreateRequest {
    name: string;
    description: string;
    permissions: { [key: string]: string[] };
}

export interface RoleUpdateRequest {
    name?: string;
    description?: string;
    permissions?: { [key: string]: string[] };
    is_active?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class RoleManagementService {
    private readonly API_URL = environment.apiUrl;

    constructor(private http: HttpClient) { }

    // Create a new role (Admin only)
    createRole(roleRequest: RoleCreateRequest): Observable<Role> {
        return this.http.post<Role>(`${this.API_URL}/roles/`, roleRequest);
    }

    // Get all roles (Admin only)
    getAllRoles(skip: number = 0, limit: number = 100, includeInactive: boolean = false): Observable<RoleListResponse> {
        const params = new HttpParams()
            .set('skip', skip.toString())
            .set('limit', limit.toString())
            .set('include_inactive', includeInactive.toString());

        return this.http.get<RoleListResponse>(`${this.API_URL}/roles/`, { params });
    }

    // Get role by ID (Admin only)
    getRoleById(roleId: string): Observable<Role> {
        return this.http.get<Role>(`${this.API_URL}/roles/${roleId}`);
    }

    // Update role (Admin only)
    updateRole(roleId: string, roleRequest: RoleUpdateRequest): Observable<Role> {
        return this.http.patch<Role>(`${this.API_URL}/roles/${roleId}`, roleRequest);
    }

    // Delete role (Admin only)
    deleteRole(roleId: string): Observable<any> {
        return this.http.delete(`${this.API_URL}/roles/${roleId}`);
    }

    // Toggle role status (Admin only)
    toggleRoleStatus(roleId: string, isActive: boolean): Observable<any> {
        return this.http.patch(`${this.API_URL}/roles/${roleId}/status`, { is_active: isActive });
    }

    // Get active roles for dropdowns (Admin only)
    getActiveRoles(): Observable<Role[]> {
        return this.http.get<Role[]>(`${this.API_URL}/roles/active/list`);
    }
}
